import React, { useMemo } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { FiTrendingUp } from 'react-icons/fi';

const OUTCOME_LABELS = ['Rejected / Ghosted', 'Applied (Waiting)', 'Interviewing', 'Offered'];
const OUTCOME_COLORS = ['#f87171', '#fbbf24', '#60a5fa', '#34d399'];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm text-xs">
      <p className="font-semibold text-gray-800">{d.company} — {d.role}</p>
      <div className="mt-1 space-y-0.5 text-gray-600">
        <p>Effort: {d.effort} actions (versions + cover letter + prep)</p>
        <p>Outcome: {OUTCOME_LABELS[d.outcome]}</p>
        {d.salary > 0 && <p>Salary: ${(d.salary / 1000).toFixed(0)}K</p>}
        <div className="flex gap-2 mt-1">
          {d.tailored && <span className="bg-green-100 text-green-700 px-1 rounded text-[10px]">Tailored</span>}
          {d.coverLetter && <span className="bg-blue-100 text-blue-700 px-1 rounded text-[10px]">Cover Letter</span>}
          {d.prepped && <span className="bg-purple-100 text-purple-700 px-1 rounded text-[10px]">Prepped</span>}
        </div>
      </div>
    </div>
  );
};

const InsightCard = ({ label, value, color }) => (
  <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
    <span className="text-xs text-gray-600">{label}</span>
    <span className="text-xs font-bold text-gray-900 ml-auto">{value}</span>
  </div>
);

const EffortOutcomeMatrix = ({ data = [], insights = {} }) => {
  // Add a stable jitter so dots stop bouncing on every re-render and so empty
  // arrays don't crash the chart.
  const scatterData = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];
    return arr.map((d, i) => {
      const jitterX = ((i * 37) % 100) / 333.3 - 0.15;
      const jitterY = ((i * 53) % 100) / 500 - 0.1;
      return {
        ...d,
        x: (d.effort ?? 0) + jitterX,
        y: (d.outcome ?? 0) + jitterY,
      };
    });
  }, [data]);

  const safeInsights = {
    tailoredInterviewRate: insights?.tailoredInterviewRate ?? 0,
    genericInterviewRate: insights?.genericInterviewRate ?? 0,
    coverLetterBoost: insights?.coverLetterBoost ?? 0,
    bestVersion: insights?.bestVersion || { name: '—', interviews: 0, apps: 0 },
  };

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden p-5 border-t-4 border-amber-500 h-full flex flex-col">
      <div className="flex items-start justify-between mb-1">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Effort vs. Outcome</h3>
          <p className="text-xs text-gray-500">Does more preparation lead to better results?</p>
        </div>
        <FiTrendingUp className="h-4 w-4 text-amber-500" />
      </div>

      {scatterData.length > 0 ? (
        <div style={{ height: 240 }} className="mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                type="number"
                dataKey="x"
                name="Effort"
                domain={[0, 5]}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                label={{ value: 'Effort (versions + prep)', position: 'bottom', fontSize: 10, fill: '#94a3b8', dy: 10 }}
              />
              <YAxis
                type="number"
                dataKey="y"
                name="Outcome"
                domain={[-0.5, 3.5]}
                ticks={[0, 1, 2, 3]}
                tickFormatter={(v) => OUTCOME_LABELS[Math.round(v)] || ''}
                tick={{ fontSize: 9, fill: '#94a3b8' }}
                width={80}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={1.5} stroke="#e2e8f0" strokeDasharray="3 3" />
              <Scatter data={scatterData}>
                {scatterData.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={OUTCOME_COLORS[Math.round(entry.outcome)] || '#94a3b8'}
                    r={Math.max(5, (entry.salary || 150000) / 30000)}
                    fillOpacity={0.7}
                    stroke={OUTCOME_COLORS[Math.round(entry.outcome)]}
                    strokeWidth={1}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="py-10 text-center text-xs text-gray-400">
          Apply to a few jobs to compare effort vs. outcome.
        </div>
      )}

      <div className="flex flex-wrap gap-3 mt-2 mb-3 text-[10px] text-gray-500">
        {OUTCOME_LABELS.map((label, i) => (
          <span key={i} className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: OUTCOME_COLORS[i] }} />
            {label}
          </span>
        ))}
        <span className="ml-2 text-gray-400">Bubble size = salary</span>
      </div>

      <div className="border-t border-gray-100 pt-3 space-y-2">
        <p className="text-xs font-semibold text-gray-700">Key Insights</p>
        <InsightCard
          label="Tailored resume → interview rate"
          value={`${(safeInsights.tailoredInterviewRate * 100).toFixed(0)}%`}
          color="#10b981"
        />
        <InsightCard
          label="Generic resume → interview rate"
          value={`${(safeInsights.genericInterviewRate * 100).toFixed(0)}%`}
          color="#f87171"
        />
        <InsightCard
          label="Cover letter boost"
          value={`+${(safeInsights.coverLetterBoost * 100).toFixed(0)}%`}
          color="#3b82f6"
        />
        <InsightCard
          label={`Best version: ${safeInsights.bestVersion.name}`}
          value={`${safeInsights.bestVersion.interviews}/${safeInsights.bestVersion.apps} interviews`}
          color="#8b5cf6"
        />
      </div>
    </div>
  );
};

export default EffortOutcomeMatrix;
